import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AgGridTable, LoadingState } from "components";
import { useReport } from "hooks";
import { S2cQuery } from "types";
import {
  formatCurrency,
  formatDateSafe,
  getVoucherUrl,
} from "utils/helpers/accounting";
import { Link } from "react-router-dom";

const today = new Date();
const formatDate = (d: Date) => d.toISOString().slice(0, 10);
const startOfYear = `${today.getFullYear()}-01-01`;
const todayString = formatDate(today);

const defaultFilters: S2cQuery = {
  from_date: startOfYear,
  to_date: todayString,
};

const S2cReport: React.FC = () => {
  const { fetchS2cHkd } = useReport();
  const [filters, setFilters] = useState<S2cQuery>(defaultFilters);
  const [submitted, setSubmitted] = useState<S2cQuery | null>(defaultFilters);

  useEffect(() => {
    fetchS2cHkd.mutate(defaultFilters);
  }, [fetchS2cHkd.mutate]);

  const handleFilterChange = useCallback((key: keyof S2cQuery, value: any) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleSubmit = useCallback(
    (event?: React.FormEvent) => {
      event?.preventDefault();
      if (!filters.from_date || !filters.to_date) {
        return;
      }
      setSubmitted(filters);
      fetchS2cHkd.mutate(filters);
    },
    [fetchS2cHkd, filters],
  );

  const responseData = fetchS2cHkd.data?.data;
  const summary = useMemo(() => {
    if (!responseData) {
      return {
        total_revenue: 0,
        total_cost: 0,
        profit_loss: 0,
        profit_margin: 0,
      };
    }
    return {
      total_revenue: responseData.total_revenue || 0,
      total_cost: responseData.total_cost || 0,
      profit_loss: responseData.profit_loss || 0,
      profit_margin: responseData.profit_margin || 0,
    };
  }, [responseData]);

  const columns = useMemo(
    () => [
      {
        headerName: "Số hiệu",
        field: "voucher_code",
        pinned: "left",
        minWidth: 140,
        cellRenderer: (params: any) => {
          const code =
            (params.data?.voucher_code as string) ??
            (params.data?.code as string) ??
            "";
          const id = params.data?.id;
          const url = getVoucherUrl(code, id);
          if (url && code) {
            return (
              <Link
                to={url}
                className="text-blue-600 hover:text-blue-800 hover:underline"
              >
                {code}
              </Link>
            );
          }
          return code;
        },
      },
      {
        headerName: "Ngày, tháng",
        field: "accounting_date",
        pinned: "left",
        minWidth: 140,
        valueFormatter: (params: any) => formatDateSafe(params.value),
      },
      {
        headerName: "Diễn giải",
        field: "description",
        minWidth: 260,
        flex: 1,
      },
      {
        headerName: "Số tiền",
        field: "amount",
        minWidth: 160,
        valueFormatter: (params: any) =>
          params.value !== undefined && params.value !== null
            ? formatCurrency(Number(params.value))
            : "",
        cellClass: "text-right font-semibold text-gray-900",
      },
    ],
    [],
  );

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      resizable: true,
      flex: 1,
      minWidth: 140,
    }),
    [],
  );

  const isLoading = fetchS2cHkd.isPending;

  return (
    <div className="space-y-6">
      {isLoading && (
        <LoadingState
          message="Đang tải báo cáo"
          description="Vui lòng chờ trong giây lát."
        />
      )}

      {/* TT152 Form Header */}
      <div className="rounded-lg border border-gray-200 bg-white shadow-sm p-6">
        <div className="flex justify-between items-start mb-4">
          <div className="text-sm text-gray-600 space-y-1">
            <p className="font-semibold">HỘ, CÁ NHÂN KINH DOANH: ..........</p>
            <p>Địa chỉ: ............................</p>
            <p>Mã số thuế: ..........................</p>
          </div>
          <div className="text-right text-sm text-gray-500 italic">
            <p className="font-semibold not-italic text-gray-700">
              Mẫu số S2c-HKD
            </p>
            <p>(Kèm theo Thông tư số 152/2025/TT-BTC</p>
            <p>ngày 31 tháng 12 năm 2025 của Bộ trưởng</p>
            <p>Bộ Tài chính)</p>
          </div>
        </div>
        <div className="text-center mb-4">
          <h2 className="text-lg font-bold text-gray-900 uppercase">
            Sổ chi tiết doanh thu, chi phí
          </h2>
          <p className="text-sm text-gray-600">
            Tên địa điểm kinh doanh: ..........................
          </p>
          <p className="text-sm text-gray-600">
            Kỳ kê khai:{" "}
            {submitted
              ? `${submitted.from_date} → ${submitted.to_date}`
              : "..............."}
          </p>
        </div>
      </div>

      {/* Filter Form */}
      <form
        onSubmit={handleSubmit}
        className="rounded-lg border border-gray-200 bg-white shadow-sm p-4 space-y-4"
      >
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              Từ ngày *
            </label>
            <input
              type="date"
              value={filters.from_date}
              onChange={(e) => handleFilterChange("from_date", e.target.value)}
              className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
              required
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              Đến ngày *
            </label>
            <input
              type="date"
              value={filters.to_date}
              onChange={(e) => handleFilterChange("to_date", e.target.value)}
              className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
              required
            />
          </div>
        </div>
        <div className="flex items-center justify-end gap-2">
          <button
            type="submit"
            className="rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-blue-700"
            disabled={isLoading}
          >
            {isLoading ? "Đang tải..." : "Xem báo cáo"}
          </button>
        </div>
      </form>

      {/* Results */}
      {submitted && (
        <div className="rounded-lg border border-gray-200 bg-white shadow-sm p-4 space-y-4">
          <p className="text-right text-sm text-gray-600 italic">
            Đơn vị tính: đồng
          </p>

          {/* 1. Doanh thu bán hàng hóa, dịch vụ */}
          <div className="bg-green-50 border border-green-200 rounded px-4 py-2">
            <h3 className="text-sm font-bold text-green-800">
              1. Doanh thu bán hàng hóa, dịch vụ
            </h3>
          </div>
          <div className="flex justify-end">
            <div className="text-right">
              <span className="text-sm text-gray-600 mr-4">Cộng (1):</span>
              <span className="text-base font-bold text-green-700">
                {formatCurrency(summary.total_revenue)}
              </span>
            </div>
          </div>

          {/* 2. Chi phí hợp lý */}
          <div className="bg-red-50 border border-red-200 rounded px-4 py-2">
            <h3 className="text-sm font-bold text-red-800">
              2. Chi phí hợp lý
            </h3>
            <p className="text-xs text-red-600 mt-1">
              a) Chi phí nguyên liệu, vật liệu, nhiên liệu, năng lượng, hàng hóa
              sử dụng vào sản xuất kinh doanh
            </p>
          </div>
          <div className="flex justify-end">
            <div className="text-right">
              <span className="text-sm text-gray-600 mr-4">Cộng (2):</span>
              <span className="text-base font-bold text-red-700">
                {formatCurrency(summary.total_cost)}
              </span>
            </div>
          </div>

          {/* Data table */}
          <AgGridTable
            rows={[]}
            columns={columns as any}
            defaultColDef={defaultColDef}
            pagination={false}
            autoHeight
          />

          {/* Grand totals */}
          <div className="border-t-2 border-gray-300 pt-3 space-y-2">
            <div className="flex justify-between items-center px-2">
              <span className="text-sm font-semibold text-gray-700">
                Chênh lệch (1) - (2):
              </span>
              <span
                className={`text-lg font-bold ${
                  summary.profit_loss >= 0 ? "text-green-700" : "text-red-700"
                }`}
              >
                {formatCurrency(summary.profit_loss)}
              </span>
            </div>
            {summary.profit_margin > 0 && (
              <div className="flex justify-between items-center px-2">
                <span className="text-sm text-gray-600">
                  Tỷ suất lợi nhuận:
                </span>
                <span className="text-base font-bold text-blue-700">
                  {summary.profit_margin.toFixed(2)} %
                </span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default S2cReport;
